/**
 * Primärer Button für CTAs (Anmelden, Absenden, Zur Registrierung).
 *
 * Verwendung wie auf Login:
 * - Primär (Anmelden): variant="primary", pill, icon, ggf. style mit theme.primaryButtonShadow
 * - Outline (Zur Registrierung): variant="outline", pill, icon, iconPosition="right",
 *   style mit theme.color.outlineButtonBg, outlineButtonBorder, theme.outlineButtonShadow
 * Farben/Schatten kommen aus dem Theme; bei Bedarf per style überschreiben.
 *
 * Auf Native: minHeight 44 (Touch-Ziel), Text mit lineHeight und includeFontPadding: false
 * für mittige Ausrichtung mit dem Icon.
 */
import { activateButtonOnKey } from "@/ui/a11y/buttonKey";
import { useTheme } from "@/theme/ThemeProvider";
import { CheckIcon } from "phosphor-react-native";
import React from "react";
import {
  ActivityIndicator,
  Platform,
  Pressable,
  Text,
  TextStyle,
  View,
  ViewStyle,
} from "react-native";

type IconProps = {
  size?: number;
  color?: string;
  weight?: "thin" | "light" | "regular" | "bold" | "fill" | "duotone";
};

type Props = {
  variant?:
    | "primary"
    | "secondary"
    | "danger"
    | "outline"
    | "ghost"
    | "dangerOutline"
    | "dangerText"
    | "dangerSolid";
  icon?: React.ComponentType<IconProps>;
  iconPosition?: "left" | "right";
  loading?: boolean;
  disabled?: boolean;
  /** Gewählte Option, z. B. RSVP oder Umfrage. Bei disabled bleibt sie am Rahmen erkennbar. */
  selected?: boolean;
  pill?: boolean;
  onPress: () => void;
  children: string;
  style?: ViewStyle;
  /** Nur setzen, wenn der Aufrufer ein eigenes Label braucht. Sonst bleibt der Button-Text der Name. */
  accessibilityLabel?: string;
  /** Nur setzen, wo die Zeile eine Rolle verlangt. Andere Aufrufer bleiben ohne Rolle. */
  accessibilityRole?: "button" | "link";
  /** Nur für den Text, z. B. flexShrink an der Einladen-Aktion. */
  labelStyle?: TextStyle;
};

export const AppButton = React.forwardRef<View, Props>(function AppButton({
  variant = "primary",
  icon: Icon,
  iconPosition = "left",
  loading = false,
  disabled = false,
  selected = false,
  pill = false,
  onPress,
  children,
  style,
  accessibilityLabel,
  accessibilityRole,
  labelStyle,
}, ref) {
  const { theme } = useTheme();
  const off = disabled || loading;

  const bg: Record<string, string> = {
    primary: theme.color.buttonBg,
    secondary: theme.color.bg,
    danger: theme.color.dangerBg,
    outline: "transparent",
    ghost: "transparent",
    dangerOutline: "transparent",
    dangerText: "transparent",
    dangerSolid: theme.color.dangerButtonBg,
  };

  const fg: Record<string, string> = {
    primary: theme.color.buttonText,
    secondary: theme.color.text,
    danger: theme.color.dangerText,
    outline: theme.color.accent,
    ghost: theme.color.accent,
    dangerOutline: theme.color.dangerText,
    dangerText: theme.color.dangerText,
    dangerSolid: theme.color.buttonText,
  };

  const borderColor: Record<string, string | undefined> = {
    primary: undefined,
    secondary: undefined,
    danger: theme.color.danger,
    outline: theme.color.accent,
    ghost: undefined,
    dangerOutline: theme.color.danger,
    dangerText: undefined,
    dangerSolid: undefined,
  };

  const textColor = fg[variant];
  const quiet =
    variant === "outline" ||
    variant === "ghost" ||
    variant === "dangerOutline" ||
    variant === "dangerText";
  const selectedOff = selected && off;
  const filledOff = off && (!quiet || selectedOff);
  const labelColor = !off
    ? textColor
    : filledOff
      ? theme.color.buttonDisabledText
      : theme.color.buttonDisabledSubtle;
  const backgroundColor = !off
    ? bg[variant]
    : filledOff
      ? theme.color.buttonDisabledBg
      : "transparent";
  const resolvedBorder = selectedOff
    ? theme.color.buttonDisabledSelectedBorder
    : !off
      ? borderColor[variant]
      : variant === "outline" || variant === "dangerOutline"
        ? theme.color.buttonDisabledSubtle
        : undefined;
  const iconSize = variant === "outline" || variant === "dangerOutline" ? 16 : 20;
  const iconEl = Icon && !loading ? (
    <Icon size={iconSize} color={labelColor} />
  ) : null;
  const selectedMark = selectedOff && !loading ? (
    <CheckIcon size={16} color={labelColor} weight="bold" />
  ) : null;

  const textFontSize =
    variant === "outline" || variant === "dangerOutline" ? theme.fontSize.small : theme.fontSize.body;
  const textLineHeight = variant === "outline" || variant === "dangerOutline" ? 18 : 22;

  return (
    <Pressable
      ref={ref}
      onPress={onPress}
      disabled={off}
      {...(accessibilityRole != null ? { accessibilityRole } : {})}
      {...(accessibilityLabel != null ? { accessibilityLabel } : {})}
      {...{
        onKeyDown: (event: {
          key?: string;
          nativeEvent?: { key?: string };
          preventDefault?: () => void;
        }) => {
          if (off) return;
          activateButtonOnKey(event, onPress);
        },
      }}
      accessibilityState={{ disabled: off, busy: loading, selected }}
      style={({ pressed }) => [
        {
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "center",
          gap: 8,
          minHeight: Platform.OS === "web" ? 32 : 44,
          backgroundColor,
          borderRadius: pill ? 999 : theme.radius.md,
          paddingVertical:
            variant === "outline" || variant === "dangerOutline" ? theme.space.xs : theme.space.sm,
          paddingHorizontal:
            variant === "outline" || variant === "dangerOutline" ? theme.space.sm : theme.space.md,
          borderWidth: selectedOff ? 2 : resolvedBorder ? 1 : 0,
          borderColor: resolvedBorder,
          opacity: 1,
          transform: [{ translateY: pressed && !off ? 1 : 0 }],
        },
        style,
      ]}
    >
      {loading ? <ActivityIndicator size="small" color={labelColor} /> : null}
      {selectedMark}
      {iconPosition === "left" ? iconEl : null}
      <Text
        style={{
          color: labelColor,
          fontWeight: "700",
          fontSize: textFontSize,
          lineHeight: textLineHeight,
          letterSpacing: 0.2,
          ...(Platform.OS === "android" ? { includeFontPadding: false } : {}),
          ...labelStyle,
        }}
      >
        {children}
      </Text>
      {iconPosition === "right" ? iconEl : null}
    </Pressable>
  );
});
